import { useMemo } from "react";
import { styled } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import HighCharts from "highcharts";
import HighChartsReact from "highcharts-react-official";
import "../HighCharts/initializeLibrary";
import { validateChartSpec, mapChartSpecToOptions } from "./chartSpecMapper";
import { ChatChartPlaceholder } from "./ChatChartPlaceholder";
import type { ChatChartProps } from "./ChatChart.types";

const ChartWrapper = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.palette.mode === "dark" ? "#404040" : "#e0e0e0"}`,
  borderRadius: "8px",
  padding: "12px",
  margin: "1em 0",
  backgroundColor: theme.palette.mode === "dark" ? "#2d2d2d" : "#f8f9fa",
  minHeight: 350,
}));

const ErrorBox = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 350,
  border: `1px solid ${theme.palette.mode === "dark" ? "#5a2d2d" : "#f5c6cb"}`,
  borderRadius: "8px",
  backgroundColor: theme.palette.mode === "dark" ? "#3a1c1c" : "#fff3f3",
  color: theme.palette.mode === "dark" ? "#f5a5a5" : "#721c24",
  fontSize: "14px",
  padding: "16px",
  textAlign: "center",
}));

type ChartResult =
  | { status: "valid"; options: HighCharts.Options }
  | { status: "parse_error"; error: string }
  | { status: "validation_error"; error: string };

function parseAndValidate(rawJson: string): ChartResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch (e) {
    return {
      status: "parse_error",
      error: e instanceof Error ? e.message : "Invalid JSON",
    };
  }

  try {
    const spec = validateChartSpec(parsed);
    const options = mapChartSpecToOptions(spec);
    return { status: "valid", options };
  } catch (e) {
    return {
      status: "validation_error",
      error: e instanceof Error ? e.message : "Invalid chart spec",
    };
  }
}

function validateAndMap(spec: unknown): ChartResult {
  try {
    const validated = validateChartSpec(spec);
    const options = mapChartSpecToOptions(validated);
    return { status: "valid", options };
  } catch (e) {
    return {
      status: "validation_error",
      error: e instanceof Error ? e.message : "Invalid chart spec",
    };
  }
}

export default function ChatChartRenderer({
  rawJson,
  spec,
  isStreaming,
}: ChatChartProps) {
  const result = useMemo(() => {
    if (spec !== undefined) return validateAndMap(spec);
    if (rawJson !== undefined) return parseAndValidate(rawJson);
    return { status: "parse_error" as const, error: "No chart data provided" };
  }, [rawJson, spec]);

  if (result.status === "valid") {
    return (
      <ChartWrapper>
        <HighChartsReact
          highcharts={HighCharts}
          options={result.options}
          immutable
          containerProps={{ style: { height: 350 } }}
        />
      </ChartWrapper>
    );
  }

  if (isStreaming) {
    return (
      <ChatChartPlaceholder>
        <CircularProgress size={20} color="inherit" />
        Generating chart...
      </ChatChartPlaceholder>
    );
  }

  return <ErrorBox>Chart error: {result.error}</ErrorBox>;
}
